'use client';

import { useEffect, useRef, useState } from 'react';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { buildSortedFeatures } from '@/utils/features/build-features';
import { Resort } from '@/utils/platform-api/__generated__/graphql';
import clsx from 'clsx';

import { FeaturesGroup } from '../feature/features-group';
import Button from '../ui/button';

interface ResortDescriptionProps {
  resort: Resort;
  cmsResort?: NodeResort;
}

const ResortDescription: React.FC<ResortDescriptionProps> = ({
  resort,
  cmsResort,
}) => {
  const resortFeatures = buildSortedFeatures(resort.features || []);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [showMore, setShowMore] = useState(false);
  useEffect(() => {
    if (textRef.current) {
      const isOverflowing =
        textRef.current.scrollHeight > textRef.current.clientHeight;
      setIsClamped(isOverflowing);
    }
  }, []);
  return (
    <>
      <div
        className={clsx(
          resortFeatures.length > 0 && 'gap-2 lg:grid lg:grid-cols-2'
        )}
      >
        <div className="flex flex-col gap-2 pr-4 lg:gap-0">
          <h3 className="text-left text-primary-main">{resort.name}</h3>
          {(resort.marketing?.primary_pitch ||
            cmsResort?.summary?.processed) && (
            <p
              ref={textRef}
              className={clsx(!showMore && 'line-clamp-[6]', 'text-black-1')}
              dangerouslySetInnerHTML={{
                __html:
                  cmsResort?.summary?.processed ||
                  resort.marketing?.primary_pitch?.replaceAll('\n', '<br />') ||
                  '',
              }}
            >
              {resort.description}
            </p>
          )}
          {isClamped && (
            <Button
              onClick={() => {
                setShowMore((prev) => !prev);
              }}
              style="text"
              className="!text-secondary-main"
            >
              <div className="flex flex-row items-center">
                {showMore ? 'Hide' : 'Read More'}
                <svg
                  className={clsx(
                    '-mt-[0.25rem] ml-[0.5rem]',
                    showMore && 'rotate-180'
                  )}
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="12"
                  viewBox="0 0 18 12"
                  fill="none"
                >
                  <path
                    d="M2 2.5L9 9.5L16 2.5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </Button>
          )}
        </div>
        {resortFeatures.length > 0 && (
          <div className="hidden h-fit flex-col gap-2 border-l pl-4 pt-4 lg:flex">
            <h4 className="text-secondary-main">Amenities</h4>
            <FeaturesGroup
              features={resortFeatures}
              className="!grid sm:!grid-cols-2"
            />
          </div>
        )}
      </div>
      {resortFeatures.length > 0 && (
        <div className="flex h-fit flex-col gap-2 pt-4 lg:hidden">
          <h4 className="text-secondary-main">Amenities</h4>
          <FeaturesGroup
            features={resortFeatures}
            className="!grid sm:!grid-cols-2"
          />
        </div>
      )}
    </>
  );
};

export default ResortDescription;
